"use client";

import {
  Menu,
  MenuTrigger,
  MenuPortal,
  MenuPositioner,
  MenuPopup,
  MenuItem,
} from "@softmax/design-system";
import type { ReactElement, ReactNode } from "react";
import type {
  AccountUser,
  AccountAgent,
  AccountMenuLink,
} from "./account-records";
import { AccountTriggerContent } from "./account-trigger-content";

export type AccountMenuViewProps = {
  readonly anchor: "masthead" | "rail";
  readonly user: AccountUser | null;
  readonly agent?: AccountAgent | null;
  readonly links: readonly AccountMenuLink[];
  readonly renderAdditionalLinks?: (
    renderItem: (link: AccountMenuLink) => ReactNode,
  ) => ReactNode;
  readonly onSignOut: () => void;
  readonly signOutState:
    | { readonly status: "idle" | "pending" }
    | { readonly status: "error"; readonly message: string };
  readonly onOpenChange?: (open: boolean) => void;
  readonly renderLink?: (link: AccountMenuLink) => ReactElement;
};

export function AccountMenuView({
  anchor,
  user,
  agent = null,
  links,
  renderAdditionalLinks,
  onSignOut,
  signOutState,
  onOpenChange,
  renderLink,
}: AccountMenuViewProps) {
  const renderItem = (link: AccountMenuLink) => (
    <MenuItem
      key={link.href}
      render={
        renderLink ? (
          renderLink(link)
        ) : (
          <a
            href={link.href}
            target={link.newTab ? "_blank" : undefined}
            rel={link.newTab ? "noopener noreferrer" : undefined}
          >
            {link.label}
          </a>
        )
      }
    >
      {link.label}
      {link.newTab && <span aria-label="(opens in a new tab)"> ↗</span>}
    </MenuItem>
  );

  return (
    <Menu modal={false} onOpenChange={onOpenChange}>
      <MenuTrigger
        aria-label="Account menu"
        variant={anchor === "rail" ? "row" : "plain"}
      >
        <AccountTriggerContent anchor={anchor} user={user} agent={agent} />
      </MenuTrigger>
      <MenuPortal>
        <MenuPositioner
          side={anchor === "rail" ? "top" : "bottom"}
          align={anchor === "rail" ? "start" : "end"}
          sideOffset={10}
          mobileSheet={anchor === "masthead"}
        >
          <MenuPopup
            size={anchor === "rail" ? "compact" : "default"}
            aria-label="Account menu"
            mobileSheet={anchor === "masthead"}
          >
            {links.map(renderItem)}
            {renderAdditionalLinks?.(renderItem)}
            {signOutState.status === "error" && (
              <output>{signOutState.message}</output>
            )}
            <MenuItem
              closeOnClick={false}
              disabled={signOutState.status === "pending"}
              onClick={onSignOut}
            >
              {signOutState.status === "pending" ? "Signing out…" : "Sign out"}
            </MenuItem>
          </MenuPopup>
        </MenuPositioner>
      </MenuPortal>
    </Menu>
  );
}
